import React, { useEffect, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router';
import NotFoundPage from './pages/NotFoundPage';
import Home from './pages/Home';
import ProductSelect from './pages/ProductSelect';
import Header from './sections/Header';
import './App.scss';
import 'swiper/css';
import Footer from './sections/Footer';
import { useDispatch, useSelector } from 'react-redux';
import Customizer from './pages/Customizer';
import ThankYou from './pages/ThankYou';
import './components/ui/Toast.scss';
import { ToastContainer, Slide } from 'react-toastify';
import { resetApp } from './utils/resetApp';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import RestoreOrderLabel from './pages/RestoreOrderLabel';
import { useApplyRefPromo } from './hooks/useApplyRefPromo';
import { usePromoCode } from './hooks/usePromoCode';
import { calculateSubtotal } from './utils/priceHandler';
import Subscriptions from './pages/Subscriptions';
import PressReleases from './pages/PressReleases';
import { trackCustomEvent } from './utils/eventsTracking/trackCustomEvent';
import { useAffiliate } from './hooks/useAffiliate';
import Partners from './pages/Partners';
import Campaigns from './pages/Campaigns';
import SingleProductPage from './pages/SingleProductPage';
import CheckoutPageSimplified from './pages/CheckoutSimplified';
import { resetCustomizedProduct } from './store/customizerSlice';
import CancelSubscriptionPage from './pages/CancelSubscription';
import BundlesPage from './pages/BundlesPage';

function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { affiliate, isLoading } = useAffiliate();
  const [mobileMenuOpened, setMobileMenuOpened] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 700);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 900);
  const colorMode = useSelector((state) => state.ui.colorMode);
  const isRestoreLabelPage = location.pathname.startsWith('/restore-label');
  const cartItems = useSelector((state) => state.cart.cartItems);
  const subtotal = calculateSubtotal(cartItems);

  const [promoCode, setPromoCode] = useState("");
  const submittedPromoCodes = useSelector(state => state.cart.submittedPromoCodes);
  const { handleApplyPromoCode, promoError, isSubmittingPromo } = usePromoCode(promoCode, setPromoCode);

  // Apply promo codes automatically from URL params and affiliates
  useApplyRefPromo(handleApplyPromoCode, subtotal, submittedPromoCodes);
  // Note: max_order_total is now handled during calculation, not by removing codes

  // Auto-apply promo code from localStorage
  useEffect(() => {
    const savedPromoCode = localStorage.getItem('promoCode');
    if (savedPromoCode && !submittedPromoCodes.some(p => p.code === savedPromoCode)) {
      console.log('Auto-applying saved promo code:', savedPromoCode);
      setPromoCode(savedPromoCode);
      // Small delay to ensure the promo code state is set before applying
      setTimeout(() => {
        handleApplyPromoCode();
        // Clear the promo code from localStorage after applying
        localStorage.removeItem('promoCode');
      }, 100);
    }
  }, [submittedPromoCodes, handleApplyPromoCode]);

  // Reset site data when version changes
  useEffect(() => {
    resetApp(dispatch);
  }, []);

  // Reset personalized product selection 
  useEffect(() => {
    const excludedPages = ["/customizer", "/checkout"];
    const isExcluded = excludedPages.some(path => location.pathname.includes(path));
    if (!isExcluded) {
      dispatch(resetCustomizedProduct());
    }
  }, [location.pathname]);

  // Is mobile/tablet
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 700);
      setIsTablet(width <= 900);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track site visit
  useEffect(() => {
    if (isLoading) return;
    trackCustomEvent('site_load', { page_visited: window.location.href }, affiliate?.code || null);
  }, [affiliate, isLoading]);

  return (
    <div className={`app-container mode-${colorMode} ${isRestoreLabelPage ? 'hide-header-footer' : ''}`}>
      <ToastContainer
        theme={colorMode}
        position="bottom-center"
        transition={Slide}
        autoClose={1500}
        hideProgressBar={true}
        closeOnClick={true}
        draggable={false}
      />
      <Header mobileMenuOpened={mobileMenuOpened} setMobileMenuOpened={setMobileMenuOpened} isMobile={isMobile} />

      <Routes>
        <Route path="/customizer/*" element={<Customizer isMobile={isMobile} />} />
        <Route path="/product-select/*" element={<ProductSelect isMobile={isMobile} />} />
        <Route path="/bundles/*" element={<BundlesPage isMobile={isMobile} />} />
        <Route path="/subscriptions/*" element={<Subscriptions isMobile={isMobile} />} />
        <Route path="/become-partner/*" element={<Partners isMobile={isMobile} />} />
        <Route path="/checkout/*" element={<CheckoutPageSimplified
          isTablet={isTablet}
          handleApplyPromoCode={handleApplyPromoCode}
          promoError={promoError}
          promoCode={promoCode}
          setPromoCode={setPromoCode}
          isSubmittingPromo={isSubmittingPromo}
        />} />
        <Route path="/thank-you/*" element={<ThankYou isMobile={isMobile} />} />
        <Route path="/terms-of-service/*" element={<TermsOfService />} />
        <Route path="/privacy-policy/*" element={<PrivacyPolicy />} />
        <Route path="/press-releases/*" element={<PressReleases />} />
        <Route path="/cancel-subscription/:cancelToken" element={<CancelSubscriptionPage />} />
        <Route path="/campaigns/:code" element={<Campaigns isMobile={isMobile} />} />
        <Route path="/restore-label/:itemId" element={<RestoreOrderLabel />} />
        <Route path="/product/:sku/*" element={<SingleProductPage isMobile={isMobile} />} />
        <Route path="/*" element={<Home isMobile={isMobile} />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      <Footer />
    </div>
  );
}

export default App;
